import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { MonthNavigator } from '../components/budget/MonthNavigator';
import { LeftToSpendCard } from '../components/today/LeftToSpendCard';
import { LimitsCard } from '../components/today/LimitsCard';
import { RecentCard } from '../components/today/RecentCard';
import { SortCard } from '../components/today/SortCard';
import { UpcomingCard } from '../components/today/UpcomingCard';
import { Button } from '../components/ui/button';
import { trpc } from '../lib/trpc';
import { usePageTitle } from '../lib/usePageTitle';
import { useViewPeriod } from '../lib/useViewPeriod';

const searchSchema = z.object({
  year: z.coerce.number().int().optional().catch(undefined),
  month: z.coerce.number().int().min(1).max(12).optional().catch(undefined),
});

export const Route = createFileRoute('/')({
  validateSearch: searchSchema,
  component: TodayPage,
});

/** Idag: the daily check-in. What's left, what needs sorting, what's coming. */
function TodayPage() {
  usePageTitle('Idag');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { year, month, ready, max, currentQuery } = useViewPeriod(search);

  const summaryQuery = trpc.period.summary.useQuery(
    { year, month },
    { retry: false, enabled: ready },
  );
  const summary = summaryQuery.data;

  const setPeriod = (y: number, m: number) =>
    void navigate({ to: '/', search: { year: y, month: m }, replace: true });

  const noHousehold =
    currentQuery.error?.data?.code === 'NOT_FOUND' ||
    summaryQuery.error?.data?.code === 'NOT_FOUND' ||
    summaryQuery.error?.data?.code === 'FORBIDDEN';
  if (noHousehold) {
    return (
      <div className="mx-auto flex max-w-sm flex-col items-center gap-4 py-20 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Välkommen till Budgie</h1>
        <p className="text-muted-foreground">Skapa ett hushåll för att börja hålla koll.</p>
        <Button asChild size="lg">
          <Link to="/household/create">Skapa hushåll</Link>
        </Button>
      </div>
    );
  }

  if (summaryQuery.error) {
    return (
      <p className="mx-auto max-w-5xl text-sm text-negative">
        Kunde inte ladda perioden: {summaryQuery.error.message}
      </p>
    );
  }

  const period = summary?.period;
  const dayMeta = period?.isCurrent && `dag ${period.daysElapsed} av ${period.daysTotal}`;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <MonthNavigator
        year={year}
        month={month}
        onChange={setPeriod}
        range={period}
        max={max}
        meta={dayMeta || undefined}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:items-start">
        <div className="space-y-4">
          <LeftToSpendCard summary={summary} />
          {period?.isCurrent && <SortCard />}
          {period && (
            <RecentCard
              startDate={period.startDate}
              lastDate={period.lastDate}
              showPending={period.isCurrent}
            />
          )}
        </div>
        {summary && (
          <div className="space-y-4">
            <LimitsCard summary={summary} />
            <UpcomingCard summary={summary} />
          </div>
        )}
      </div>
    </div>
  );
}
